/**
 * Attach team-leader flags to the session user.
 *
 * Source order:
 * 1. JWT claims (backend embeds role / permissions here)
 * 2. GET /auth/me (server is source of truth for role)
 * 3. GET /team-leaders/:id or /me (self-read)
 * Stored user JSON is never trusted for role / isSuperAdmin.
 */

import { AUTH_CONFIG } from '@/config/constants';
import { ENDPOINTS, getEndpointPath } from '@/api/endpoints';
import { isTeamLeader } from '../lib/roleUtils';
import {
  extractPermissionsFromPayload,
  mergePermissionMaps,
  pickKnownPermissions,
} from './normalizePermissions';
import { readAccessTokenClaims } from './tokenClaims';

export function persistUser(user) {
  if (!user) return user;
  localStorage.setItem(AUTH_CONFIG.USER_STORAGE_KEY, JSON.stringify(user));
  return user;
}

export function applyTeamLeaderProfile(user, profile = {}, extraPermissions = {}) {
  if (!user) return user;
  const departmentIds = Array.isArray(profile.teamLeaderDepartments)
    ? profile.teamLeaderDepartments
    : Array.isArray(profile.departments)
      ? profile.departments.map((dept) => dept.id || dept._id).filter(Boolean)
      : [];

  const fromProfile = extractPermissionsFromPayload(profile);
  const savedMap = [profile.permissions, profile.teamLeaderPermissions, profile.permissionFlags]
    .find((value) => value && typeof value === 'object');
  const permissions = savedMap
    ? pickKnownPermissions(fromProfile)
    : mergePermissionMaps(user.permissions, extraPermissions, fromProfile);

  return {
    ...user,
    id: user.id || user.userId,
    userId: user.userId || user.id,
    role: profile.role || user.role,
    permissions,
    teamLeaderDepartments: departmentIds.length
      ? departmentIds
      : (user.teamLeaderDepartments || []),
  };
}

export function applyAccessTokenClaims(user, token = localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY)) {
  const claims = readAccessTokenClaims(token);
  if (!user) return user;
  if (!claims) {
    return {
      ...applyTeamLeaderProfile(user),
      isSuperAdmin: false,
      isImpersonation: false,
    };
  }
  const isSuperAdmin = Boolean(claims.isSuperAdmin) || claims.role === 'superAdmin';
  const role = isSuperAdmin ? 'superAdmin' : (claims.role || user.role);
  return {
    ...applyTeamLeaderProfile(user, {
      role,
      permissions: claims.permissions,
      teamLeaderDepartments: claims.teamLeaderDepartments,
    }),
    isSuperAdmin,
    isImpersonation: Boolean(claims.isImpersonation),
  };
}

function profileFromResponse(response) {
  return response?.data?.data || response?.data || {};
}

export async function enrichUserWithTeamLeaderAccess(user) {
  if (!user) return user;
  let next = applyAccessTokenClaims(user);
  if (!isTeamLeader(next.role)) return persistUser(next);

  const userId = next.userId || next.id;
  try {
    const httpClient = (await import('@/utils/httpClient')).default;
    const path = userId
      ? getEndpointPath(ENDPOINTS.TEAM_LEADERS.BY_ID, userId)
      : getEndpointPath(ENDPOINTS.TEAM_LEADERS.ME);
    const response = await httpClient.get(path);
    next = applyTeamLeaderProfile(next, profileFromResponse(response));
  } catch {
    // JWT / login flags remain if self-read is unavailable.
  }

  return persistUser(next);
}

export async function hydrateSessionUser(user) {
  // Role / isSuperAdmin come from JWT claims (applyAccessTokenClaims).
  // Do not call /auth/me here until the backend endpoint is guaranteed;
  // a 401 would log the user out through the HTTP interceptor.
  return enrichUserWithTeamLeaderAccess(user);
}
